// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { SnackbarKey, useSnackbar } from 'notistack';
import React, { PropsWithChildren, useCallback, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Close } from '@mui/icons-material';
import {
  Backdrop,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { ErrorContext } from '../contexts/ErrorContext';
import { getFriendlyError } from '../utils/parseApiError';

const ErrorWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const [errorContext, setErrorContext] = useState<Error>();
  const [displayError, setDisplayError] = useState(false);

  const onDetails = useCallback(
    (key: SnackbarKey) => {
      setDisplayError(true);
      closeSnackbar(key);
    },
    [closeSnackbar],
  );

  const onClose = useCallback(
    (key: SnackbarKey) => {
      closeSnackbar(key);
    },
    [closeSnackbar],
  );

  const enqueueError = React.useCallback(
    (err: Error) => {
      setErrorContext(err);
      const message = err.message || getFriendlyError(err).message;
      enqueueSnackbar(message, {
        variant: 'utilities',
        onUtilitiesDetails: onDetails,
        onUtilitiesClose: onClose,
        preventDuplicate: true,
        persist: true,
      });
    },
    [enqueueSnackbar, onClose, onDetails],
  );

  const Fallback: React.FC<FallbackProps> = useCallback(
    ({ error, resetErrorBoundary }) => {
      const errorT = error as Error;
      setErrorContext(errorT);
      enqueueSnackbar(errorT.message, {
        variant: 'utilities',
        onUtilitiesDetails: onDetails,
        onUtilitiesClose: onClose,
        onEnter: resetErrorBoundary,
        preventDuplicate: true,
        persist: true,
      });
      return <div />;
    },
    [enqueueSnackbar, onClose, onDetails],
  );
  const value = React.useMemo(
    () => ({
      enqueueError,
    }),
    [enqueueError],
  );
  const friendly = errorContext ? getFriendlyError(errorContext) : undefined;
  return (
    <ErrorContext.Provider value={value}>
      <ErrorBoundary
        FallbackComponent={Fallback}
        onError={(error) => {
          setErrorContext(error);
        }}
      >
        <Backdrop open={displayError}>
          <Dialog
            open={displayError}
            onClose={() => setDisplayError(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
              Error
              <IconButton size="small" onClick={() => setDisplayError(false)}>
                <Close sx={{ color: 'text.primary' }} fontSize="medium" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography>{friendly?.message ?? errorContext?.message}</Typography>
            </DialogContent>
          </Dialog>
        </Backdrop>
        {children}
      </ErrorBoundary>
    </ErrorContext.Provider>
  );
};

export default ErrorWrapper;
